import * as React from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import _debounce from 'lodash/debounce';
import { useTranslation } from 'react-i18next';
import { Spinner, SpinnerContainer } from 'components/Overview/Styles';
import config from 'config/config';
import { ReactComponent as KonnectIcon } from '../../assets/icons/appIconSuperUserPage.svg';
import {
  LoginPage,
  LoginWrapper,
  SearchInput,
  DropdownList,
  DropdownItem,
  ConfirmButton,
  EmptyBox,
  ErrorMessageWrapper,
  IconWrapper,
  InputContainer,
} from './SuperUserStyles';

const SuperUserLoginPage = props => {
  const MIN_SEARCH_LENGTH = 3;
  const { location } = props;
  const [value, setValue] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState(undefined);
  const [userList, setUserList] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);

  const params = new URLSearchParams(location.search);
  const sessionToken = params.get('sessionToken');
  const state = params.get('state');

  const [selectedUser, setSelectedUser] = useState(undefined);

  // Create refs for the elements
  const continueButtonRef = useRef(null);
  const inputRef = useRef(null);
  const dropdownRef = useRef(null);
  const itemRefs = useRef<(HTMLLIElement | null)[]>([]);

  // Create a debounced request function that will wait 500ms after the last call before executing
  const debouncedRequest = useCallback(
    _debounce(value => {
      request(value);
    }, 500),
    [sessionToken]
  );

  // Clean up the debounced function when component unmounts
  useEffect(() => {
    return () => {
      debouncedRequest.cancel();
    };
  }, [debouncedRequest]);

  // Scroll selected dropdown item into view on keyboard navigation
  useEffect(() => {
    if (selectedIndex >= 0 && itemRefs.current[selectedIndex]) {
      itemRefs.current[selectedIndex].scrollIntoView({ block: 'nearest' });
    }
  }, [selectedIndex]);

  // Focus the button when selectedUser changes
  useEffect(() => {
    if (selectedUser && continueButtonRef.current) {
      continueButtonRef.current.focus();
    }
  }, [selectedUser]);
  const { t } = useTranslation();
  const onChangeHandler = event => {
    setErrorMessage('');
    const newValue = event.target.value;
    if (newValue.length < MIN_SEARCH_LENGTH || newValue === '') {
      setSelectedUser(undefined);
      setUserList([]);
      setShowDropdown(false);
    } else {
      setShowDropdown(true);
    }

    setValue(newValue);
    debouncedRequest(newValue);
  };

  const request = value => {
    if (value.length < MIN_SEARCH_LENGTH || value === '') {
      setSelectedUser(undefined);
      setUserList([]);
      return;
    }

    setIsLoading(true);
    fetch(
      `${config.API_URL}/api/super-users/get-super-user-tenants?sessionToken=${sessionToken}&namePattern=${value}`
    )
      .then(response => response.json())
      .then(result => {
        if (result.status >= 400) {
          setErrorMessage('Something Went Wrong, try later');
        } else {
          const sortedUsers = result.sort((user1, user2) => {
            return user1.name.toLowerCase() > user2.name.toLowerCase() ? 1 : -1;
          });
          setUserList(sortedUsers);
          if (!sortedUsers.length) {
            setErrorMessage('There are no users in your account with this name');
          }
          setIsLoading(false);
        }
      })
      .catch(error => {
        setErrorMessage('Something Went Wrong, try later');
        setIsLoading(false);
      });
  };

  const onConfirmClickHandler = () => {
    if (!selectedUser) return;
    setIsLoading(true);
    fetch(
      `${config.API_URL}/api/super-users/login-callback?selectedTenantId=${selectedUser.id}&state=${state}&sessionToken=${sessionToken}`
    )
      .then(result => result.json())
      .then(res => {
        window.location.replace(res.callbackUrl);
      })
      .catch(error => console.log(error));
  };

  const selectOption = option => {
    const selectedUser = userList.find(user => user.name === option);
    if (selectedUser) {
      setSelectedUser(selectedUser);
      setValue(option);
      setShowDropdown(false);

      // Focus the continue button immediately
      if (continueButtonRef.current) {
        continueButtonRef.current.focus();
      }
    }
  };

  const handleKeyDown = event => {
    if (!showDropdown || userList.length === 0) return;

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        setSelectedIndex(prev => (prev < userList.length - 1 ? prev + 1 : prev));
        break;
      case 'ArrowUp':
        event.preventDefault();
        setSelectedIndex(prev => (prev > 0 ? prev - 1 : 0));
        break;
      case 'Enter':
        event.preventDefault();
        if (selectedIndex >= 0) {
          selectOption(userList[selectedIndex].name);
        }
        break;
      case 'Escape':
        setShowDropdown(false);
        break;
    }
  };

  return (
    <LoginPage>
      <LoginWrapper>
        <EmptyBox></EmptyBox>
        <IconWrapper>
          <KonnectIcon />
        </IconWrapper>

        <InputContainer>
          <SearchInput
            placeholder={t('superUser.startTypingCustomerName')}
            ref={inputRef}
            value={value}
            onChange={onChangeHandler}
            onKeyDown={handleKeyDown}
            onFocus={() => value.length >= MIN_SEARCH_LENGTH && setShowDropdown(true)}
          />

          {showDropdown && userList.length > 0 && (
            <DropdownList ref={dropdownRef}>
              {userList.map((user, index) => (
                <DropdownItem
                  key={user.id}
                  ref={el => { itemRefs.current[index] = el; }}
                  className={index === selectedIndex ? 'selected' : ''}
                  onClick={() => selectOption(user.name)}>
                  {user.name}
                </DropdownItem>
              ))}
            </DropdownList>
          )}
        </InputContainer>

        <ConfirmButton
          ref={continueButtonRef}
          disabled={!selectedUser}
          style={{
            textTransform: 'none',
          }}
          variant="contained"
          onClick={onConfirmClickHandler}>
          Continue
        </ConfirmButton>
        {errorMessage && <ErrorMessageWrapper>{errorMessage}</ErrorMessageWrapper>}
        {isLoading && <SpinnerContainer>{Spinner(30)}</SpinnerContainer>}
      </LoginWrapper>
    </LoginPage>
  );
};
export default SuperUserLoginPage;
